import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import RackLayouts from '../components/RackLayouts';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Plus, 
  Upload, 
  Save, 
  Trash2, 
  ZoomIn, 
  ZoomOut, 
  Move,
  Edit,
  Monitor,
  Laptop,
  Printer,
  Wifi,
  Phone,
  Server,
  Network,
  Camera,
  Grid3x3,
  Pencil
} from 'lucide-react';

const deviceTypes = [
  { value: 'desktop', label: 'Desktop PC', icon: Monitor, color: '#3b82f6', size: 40 },
  { value: 'laptop', label: 'Laptop', icon: Laptop, color: '#8b5cf6', size: 35 },
  { value: 'printer', label: 'Printer', icon: Printer, color: '#ef4444', size: 45 },
  { value: 'access_point', label: 'Access Point', icon: Wifi, color: '#10b981', size: 30 },
  { value: 'phone', label: 'VoIP Phone', icon: Phone, color: '#f59e0b', size: 25 },
  { value: 'server', label: 'Server', icon: Server, color: '#6366f1', size: 50 },
  { value: 'router', label: 'Router/Switch', icon: Network, color: '#ec4899', size: 40 },
  { value: 'camera', label: 'Camera', icon: Camera, color: '#14b8a6', size: 30 }
];

const OfficeLayouts = ({ clientId: propClientId }) => {
  const { clientId: paramClientId } = useParams();
  const clientId = propClientId || paramClientId;

  // Debug logging
  React.useEffect(() => {
    console.log('✅ OfficeLayouts mounted, clientId:', clientId);
  }, [clientId]);
  const canvasRef = useRef(null);
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showNewLayoutDialog, setShowNewLayoutDialog] = useState(false);
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const renderCountRef = useRef(0);
  
  // Track renders
  renderCountRef.current += 1;
  console.log('🔄 OfficeLayouts render #' + renderCountRef.current, 'showNewLayoutDialog:', showNewLayoutDialog);

  // Debug dialog state
  React.useEffect(() => {
    console.log('🔔 showNewLayoutDialog changed to:', showNewLayoutDialog);
    console.log('🔔 Dialog state update detected at:', new Date().toISOString());
  }, [showNewLayoutDialog]);
  
  // Canvas state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [tool, setTool] = useState('select'); // select, draw_wall, place_asset, draw_rect, draw_circle
  const [drawingWall, setDrawingWall] = useState(null);
  const [tempDrawing, setTempDrawing] = useState(null); // For live preview while drawing
  const [draggedAsset, setDraggedAsset] = useState(null);
  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [hoveredWall, setHoveredWall] = useState(null);
  const [selectedWall, setSelectedWall] = useState(null);
  
  const [newLayout, setNewLayout] = useState({
    name: '',
    description: '',
    width: 1200,
    height: 800
  });

  const [newAsset, setNewAsset] = useState({
    name: '',
    device_type: 'desktop',
    x: 100,
    y: 100,
    linked_asset_id: null,
    linked_user: '',
    manual: true
  });

  useEffect(() => {
    fetchLayouts();
    fetchAgents();
  }, [clientId]);

  useEffect(() => {
    if (selectedLayout) {
      // Use requestAnimationFrame for smoother rendering

  // Handle keyboard events for deletion
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedWall !== null && selectedLayout) {
          // Delete selected wall
          const newWalls = selectedLayout.walls.filter((_, index) => index !== selectedWall);
          setSelectedLayout({
            ...selectedLayout,
            walls: newWalls
          });
          setSelectedWall(null);
          toast.success('Wall deleted');
        }
      }
      
      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedWall(null);
        setDrawingWall(null);
        setTempDrawing(null);
        if (tool !== 'select') {
          setTool('select');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedWall, selectedLayout, tool]);

      requestAnimationFrame(() => {
        drawCanvas();
      });
    }
  }, [selectedLayout, zoom, pan, hoveredAsset, tempDrawing]);

  const fetchLayouts = async () => {
    try {
      const response = await api.get(`/clients/${clientId}/layouts`);
      setLayouts(response.data);
      if (response.data.length > 0 && !selectedLayout) {
        setSelectedLayout(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load layouts');
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents');
      const clientAgents = response.data.filter(a => a.client_id === clientId);
      setAgents(clientAgents);
    } catch (error) {
      console.error('Failed to load agents');
    }
  };

  const handleCreateLayout = async () => {
    try {
      if (!newLayout.name.trim()) {
        toast.error('Please enter a layout name');
        return;
      }
      
      await api.post(`/clients/${clientId}/layouts`, newLayout);
      toast.success('Layout created');
      setShowNewLayoutDialog(false);
      fetchLayouts();
      setNewLayout({ name: '', description: '', width: 1200, height: 800 });
    } catch (error) {
      console.error('Layout creation error:', error);
      toast.error('Failed to create layout: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUploadBackground = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(
        `/clients/${clientId}/layouts/${selectedLayout.id}/upload-background`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setSelectedLayout({
        ...selectedLayout,
        background_image: response.data.image_url,
        background_type: 'upload'
      });
      
      toast.success('Background uploaded');
    } catch (error) {
      toast.error('Failed to upload background');
    }
  };

  const handleSaveLayout = async () => {
    try {
      await api.put(`/clients/${clientId}/layouts/${selectedLayout.id}`, selectedLayout);
      toast.success('Layout saved');
      fetchLayouts();
    } catch (error) {
      toast.error('Failed to save layout');
    }
  };

  const handleDeleteLayout = async (layoutId) => {
    if (!window.confirm('Delete this layout?')) return;
    
    try {
      await api.delete(`/clients/${clientId}/layouts/${layoutId}`);
      toast.success('Layout deleted');
      setSelectedLayout(null);
      fetchLayouts();
    } catch (error) {
      toast.error('Failed to delete layout');
    }
  };

  const handleAddAsset = () => {
    const asset = {
      id: Date.now().toString(),
      ...newAsset,
      x: 100 + (selectedLayout.assets?.length || 0) * 20,
      y: 100 + (selectedLayout.assets?.length || 0) * 20
    };

    setSelectedLayout({
      ...selectedLayout,
      assets: [...(selectedLayout.assets || []), asset]
    });

    setShowAssetDialog(false);
    setNewAsset({
      name: '',
      device_type: 'desktop',
      x: 100,
      y: 100,
      linked_asset_id: null,
      linked_user: '',
      manual: true
    });
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !selectedLayout) return;

    const ctx = canvas.getContext('2d');
    const width = selectedLayout.width;
    const height = selectedLayout.height;

    canvas.width = width;
    canvas.height = height;

    // Clear canvas
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, width, height);

    // Apply zoom and pan
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw background image if exists
    if (selectedLayout.background_image) {
      const img = new Image();
      img.src = selectedLayout.background_image;
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        drawWallsAndAssets(ctx);
      };
    } else {
      drawWallsAndAssets(ctx);

  // Helper function to calculate distance from point to line
  const distanceToLine = (px, py, x1, y1, x2, y2) => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) param = dot / lenSq;
    
    let xx, yy;
    
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

    }

    ctx.restore();
  };

  const drawWallsAndAssets = (ctx) => {
    // Draw grid
    drawGrid(ctx);

    // Draw walls
    if (selectedLayout.walls) {
      selectedLayout.walls.forEach((wall, index) => {
        const isHovered = hoveredWall === index;
        const isSelected = selectedWall === index;
        
        ctx.strokeStyle = isSelected ? '#3b82f6' : (isHovered ? '#ef4444' : '#1e293b');
        ctx.lineWidth = (isSelected || isHovered) ? 5 : 3;
        ctx.beginPath();
        ctx.moveTo(wall.x1, wall.y1);
        ctx.lineTo(wall.x2, wall.y2);
        ctx.stroke();
        
        // Draw endpoints for selected or hovered wall
        if (isSelected || isHovered) {
          ctx.fillStyle = isSelected ? '#3b82f6' : '#ef4444';
          ctx.beginPath();
          ctx.arc(wall.x1, wall.y1, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(wall.x2, wall.y2, 6, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    }

    // Draw live drawing preview
    if (tempDrawing) {
      ctx.save();
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.setLineDash([5, 5]);
      
      if (tempDrawing.type === 'wall') {
        ctx.beginPath();
        ctx.moveTo(tempDrawing.x1, tempDrawing.y1);
        ctx.lineTo(tempDrawing.x2, tempDrawing.y2);
        ctx.stroke();
      } else if (tempDrawing.type === 'rect') {
        const width = tempDrawing.x2 - tempDrawing.x1;
        const height = tempDrawing.y2 - tempDrawing.y1;
        ctx.strokeRect(tempDrawing.x1, tempDrawing.y1, width, height);
      } else if (tempDrawing.type === 'circle') {
        const radius = Math.sqrt(
          Math.pow(tempDrawing.x2 - tempDrawing.x1, 2) + 
          Math.pow(tempDrawing.y2 - tempDrawing.y1, 2)
        );
        ctx.beginPath();
        ctx.arc(tempDrawing.x1, tempDrawing.y1, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
      
      ctx.restore();
    }

    // Draw assets
    if (selectedLayout.assets) {
      selectedLayout.assets.forEach(asset => {
        drawAsset(ctx, asset, asset.id === hoveredAsset?.id);
      });
    }
  };

  const drawGrid = (ctx) => {
    const gridSize = 50;
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;

    for (let x = 0; x < selectedLayout.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, selectedLayout.height);
      ctx.stroke();
    }

    for (let y = 0; y < selectedLayout.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(selectedLayout.width, y);
      ctx.stroke();
    }
  };

  const drawAsset = (ctx, asset, isHovered) => {
    const deviceConfig = deviceTypes.find(d => d.value === asset.device_type) || deviceTypes[0];
    const size = deviceConfig.size;

    // Get online status
    const linkedAgent = agents.find(a => a.id === asset.linked_asset_id);
    const isOnline = linkedAgent?.status === 'online';

    // Draw device icon background circle
    ctx.fillStyle = isHovered ? deviceConfig.color : '#fff';
    ctx.strokeStyle = deviceConfig.color;
    ctx.lineWidth = isHovered ? 4 : 3;

    const iconRadius = size / 2 + 5;
    ctx.beginPath();
    ctx.arc(asset.x, asset.y, iconRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Draw simplified icon representation
    ctx.save();
    ctx.fillStyle = isHovered ? '#fff' : deviceConfig.color;
    ctx.strokeStyle = isHovered ? '#fff' : deviceConfig.color;
    ctx.lineWidth = 2;
    
    // Draw icon based on device type
    const iconSize = size * 0.6;
    switch(asset.device_type) {
      case 'desktop':
      case 'laptop':
        // Monitor/Screen
        ctx.strokeRect(asset.x - iconSize/2, asset.y - iconSize/2, iconSize, iconSize * 0.7);
        ctx.fillRect(asset.x - iconSize/6, asset.y + iconSize/4, iconSize/3, iconSize/6);
        break;
      case 'printer':
        // Printer shape
        ctx.fillRect(asset.x - iconSize/2, asset.y - iconSize/4, iconSize, iconSize/2);
        ctx.strokeRect(asset.x - iconSize/3, asset.y - iconSize/2, iconSize * 0.66, iconSize/4);
        break;
      case 'access_point':
      case 'router':
        // Signal waves
        for (let i = 1; i <= 3; i++) {
          ctx.beginPath();
          ctx.arc(asset.x, asset.y, iconSize/6 * i, -Math.PI/3, Math.PI/3);
          ctx.stroke();
        }
        break;
      case 'server':
        // Server rack
        ctx.fillRect(asset.x - iconSize/2, asset.y - iconSize/2, iconSize, iconSize * 0.25);
        ctx.fillRect(asset.x - iconSize/2, asset.y - iconSize/6, iconSize, iconSize * 0.25);
        ctx.fillRect(asset.x - iconSize/2, asset.y + iconSize/8, iconSize, iconSize * 0.25);
        break;
      case 'phone':
        // Phone handset
        ctx.beginPath();
        ctx.arc(asset.x - iconSize/4, asset.y - iconSize/4, iconSize/6, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(asset.x + iconSize/4, asset.y + iconSize/4, iconSize/6, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(asset.x - iconSize/6, asset.y - iconSize/6);
        ctx.lineTo(asset.x + iconSize/6, asset.y + iconSize/6);
        ctx.stroke();
        break;
      case 'camera':
        // Camera lens
        ctx.beginPath();
        ctx.arc(asset.x, asset.y, iconSize/3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillRect(asset.x + iconSize/4, asset.y - iconSize/6, iconSize/6, iconSize/8);
        break;
      default:
        // Default icon - circle
        ctx.beginPath();
        ctx.arc(asset.x, asset.y, iconSize/4, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();

    // Draw online status indicator
    ctx.fillStyle = isOnline ? '#10b981' : '#ef4444';
    ctx.beginPath();
    ctx.arc(asset.x + iconRadius - 6, asset.y - iconRadius + 6, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Always draw asset name and user below icon
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    
    // Use linked agent name if available, otherwise use asset name
    const label = (linkedAgent?.friendly_name || linkedAgent?.hostname || asset.name) || deviceConfig.label;
    const truncatedLabel = label.length > 12 ? label.substring(0, 12) + '...' : label;
    
    // Get current/last logged in user
    const currentUser = linkedAgent?.primary_user || asset.linked_user || '';
    const truncatedUser = currentUser.length > 15 ? currentUser.substring(0, 15) + '...' : currentUser;
    
    // Calculate text dimensions
    ctx.font = 'bold 11px sans-serif';
    const labelWidth = Math.min(ctx.measureText(truncatedLabel).width + 8, 80);
    
    ctx.font = '10px sans-serif';
    const userWidth = currentUser ? Math.min(ctx.measureText(truncatedUser).width + 8, 80) : 0;
    
    const maxWidth = Math.max(labelWidth, userWidth);
    const boxHeight = currentUser ? 32 : 16;
    
    // Background for text
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.fillRect(asset.x - maxWidth/2, asset.y + iconRadius + 2, maxWidth, boxHeight);
    
    // Border
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    ctx.strokeRect(asset.x - maxWidth/2, asset.y + iconRadius + 2, maxWidth, boxHeight);
    
    // Asset name
    ctx.font = 'bold 11px sans-serif';
    ctx.fillStyle = '#1e293b';
    ctx.fillText(truncatedLabel, asset.x, asset.y + iconRadius + 6, 80);
    
    // User name (if available)
    if (currentUser) {
      ctx.font = '10px sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.fillText(truncatedUser, asset.x, asset.y + iconRadius + 20, 80);
    }
    
    ctx.restore();

    // Draw additional info if hovered
    if (isHovered) {
      const user = asset.linked_user || (linkedAgent?.friendly_name || '');
      
      if (user) {
        ctx.save();
        ctx.font = '10px sans-serif';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        const userWidth = ctx.measureText(user).width + 12;
        ctx.fillRect(asset.x - userWidth/2, asset.y + iconRadius + 20, userWidth, 18);
        
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.fillText(user, asset.x, asset.y + iconRadius + 28);
        ctx.restore();
      }
    }
  };

  const handleCanvasMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    if (tool === 'pan' || e.button === 1) { // Middle mouse button
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    } else if (tool === 'draw_wall' || tool === 'draw_rect' || tool === 'draw_circle') {
      if (!drawingWall) {
        setDrawingWall({ x1: x, y1: y, type: tool });
      } else {
        const newWall = { ...drawingWall, x2: x, y2: y };
        setSelectedLayout({
          ...selectedLayout,
          walls: [...(selectedLayout.walls || []), newWall]
        });
        setDrawingWall(null);
        setTempDrawing(null);
        // Keep the same tool active for continuous drawing
      }
    } else if (tool === 'select') {
      // Check if clicking on a wall
      let clickedWallIndex = null;
      if (selectedLayout.walls) {
        selectedLayout.walls.forEach((wall, index) => {
          // Check distance from point to line
          const distance = distanceToLine(x, y, wall.x1, wall.y1, wall.x2, wall.y2);
          if (distance < 10) { // 10 pixel threshold
            clickedWallIndex = index;
          }
        });
      }
      
      if (clickedWallIndex !== null) {
        setSelectedWall(clickedWallIndex);
        setHoveredAsset(null);
      } else {
        // Check if clicking on an asset
        const clickedAsset = selectedLayout.assets?.find(asset => {
          const deviceConfig = deviceTypes.find(d => d.value === asset.device_type) || deviceTypes[0];
          const size = deviceConfig.size;
          return x >= asset.x - size/2 && x <= asset.x + size/2 &&
                 y >= asset.y - size/2 && y <= asset.y + size/2;
        });

        if (clickedAsset) {
          setDraggedAsset({ ...clickedAsset, offsetX: x - clickedAsset.x, offsetY: y - clickedAsset.y });
        }
        setSelectedWall(null); // Deselect wall when clicking elsewhere
      }
    }
  };

  const handleCanvasMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    } else if (drawingWall) {
      // Show live preview of shape being drawn
      const drawType = drawingWall.type === 'draw_rect' ? 'rect' : drawingWall.type === 'draw_circle' ? 'circle' : 'wall';
      setTempDrawing({ type: drawType, x1: drawingWall.x1, y1: drawingWall.y1, x2: x, y2: y });
    } else if (draggedAsset) {
      const newAssets = selectedLayout.assets.map(asset => 
        asset.id === draggedAsset.id 
          ? { ...asset, x: x - draggedAsset.offsetX, y: y - draggedAsset.offsetY }
          : asset
      );
      setSelectedLayout({ ...selectedLayout, assets: newAssets });
    } else {
      // Check hover
      const hovered = selectedLayout.assets?.find(asset => {
        const deviceConfig = deviceTypes.find(d => d.value === asset.device_type) || deviceTypes[0];
        const size = deviceConfig.size;
        return x >= asset.x - size/2 && x <= asset.x + size/2 &&
               y >= asset.y - size/2 && y <= asset.y + size/2;
      });
      setHoveredAsset(hovered || null);
    }
  };

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
    setDraggedAsset(null);
    setTempDrawing(null); // Clear preview after finishing draw
  };

  const handleZoom = (delta) => {
    setZoom(prev => Math.max(0.25, Math.min(3, prev + delta)));
  };

  // Callback to open dialog - prevents stale closure issues and forces immediate render
  const openNewLayoutDialog = React.useCallback(() => {
    console.log('🚀 openNewLayoutDialog called');
    flushSync(() => {
      setShowNewLayoutDialog(prev => {
        console.log('🚀 Setting dialog state from', prev, 'to true');
        return true;
      });
    });
    console.log('🚀 flushSync complete');
  }, []);

  const officeLayoutsContent = () => {
    if (!selectedLayout && layouts.length === 0) {
      return (
        <div className="p-4 sm:p-6 lg:p-8">
          <Card>
            <CardContent className="py-12 text-center">
              <Grid3x3 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No office layouts yet</h3>
              <p className="text-slate-500 mb-6">Create your first office layout to visualize asset placement</p>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('📍 Create First Layout clicked');
                  openNewLayoutDialog();
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Layout
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Office Layouts</h2>
            <p className="text-sm text-slate-600">Visual asset placement and floor plans</p>
          </div>
        <div className="flex flex-wrap gap-2">
          <Select value={selectedLayout?.id} onValueChange={(id) => setSelectedLayout(layouts.find(l => l.id === id))}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select layout" />
            </SelectTrigger>
            <SelectContent>
              {layouts.map(layout => (
                <SelectItem key={layout.id} value={layout.id}>
                  {layout.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log('📍 New Layout clicked (header)');
            openNewLayoutDialog();
          }}>
            <Plus className="w-4 h-4 mr-2" />
            New
          </Button>
          {selectedLayout && (
            <>
              <Button variant="outline" onClick={handleSaveLayout}>
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" className="text-red-600" onClick={() => handleDeleteLayout(selectedLayout.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {selectedLayout && (
        <div className="grid lg:grid-cols-4 gap-4">
          {/* Toolbar */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">Tools</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tool Selection */}
              <div className="space-y-2">
                <Label>Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={tool === 'select' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTool('select')}
                  >
                    <Move className="w-4 h-4 mr-1" />
                    Select
                  </Button>
                  <Button
                    variant={tool === 'draw_wall' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTool('draw_wall')}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Wall
                  </Button>
                  <Button
                    variant={tool === 'draw_rect' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTool('draw_rect')}
                    title="Draw Rectangle"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" strokeWidth="2" rx="2"/>
                    </svg>
                    Rect
                  </Button>
                  <Button
                    variant={tool === 'draw_circle' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTool('draw_circle')}
                    title="Draw Circle"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="9" strokeWidth="2"/>
                    </svg>
                    Circle
                  </Button>
                </div>
              </div>

              {/* Zoom Controls */}
              <div className="space-y-2">
                <Label>Zoom: {Math.round(zoom * 100)}%</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleZoom(-0.25)}>
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleZoom(0.25)}>
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>
                    Reset
                  </Button>
                </div>
              </div>

              {/* Upload Background */}
              <div className="space-y-2">
                <Label>Background</Label>
                <Button variant="outline" size="sm" className="w-full" onClick={() => document.getElementById('bg-upload').click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Image
                </Button>
                <input
                  id="bg-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUploadBackground}
                />
              </div>

              {/* Selected Wall Info */}
              {selectedWall !== null && (
                <div className="p-3 bg-blue-50 border border-blue-300 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-blue-900">Wall Selected</Label>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        const newWalls = selectedLayout.walls.filter((_, index) => index !== selectedWall);
                        setSelectedLayout({ ...selectedLayout, walls: newWalls });
                        setSelectedWall(null);
                        toast.success('Wall deleted');
                      }}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                  <p className="text-xs text-blue-700">
                    Press <kbd className="px-1 py-0.5 bg-white rounded border">Delete</kbd> or <kbd className="px-1 py-0.5 bg-white rounded border">Backspace</kbd> to remove
                  </p>
                </div>
              )}

              {/* Add Asset */}
              <Button className="w-full" onClick={() => setShowAssetDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>

              {/* Asset List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                <Label>Assets ({selectedLayout.assets?.length || 0})</Label>
                {selectedLayout.assets?.map(asset => {
                  const deviceConfig = deviceTypes.find(d => d.value === asset.device_type);
                  const Icon = deviceConfig?.icon || Monitor;
                  const linkedAgent = agents.find(a => a.id === asset.linked_asset_id);
                  const isOnline = linkedAgent?.status === 'online';
                  
                  return (
                    <div key={asset.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: deviceConfig?.color }} />
                        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="truncate">{asset.name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedLayout({
                            ...selectedLayout,
                            assets: selectedLayout.assets.filter(a => a.id !== asset.id)
                          });
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Canvas */}
          <Card className="lg:col-span-3">
            <CardContent className="p-2">
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-100">
                <canvas
                  ref={canvasRef}
                  className="cursor-crosshair"
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {tool === 'select' && 'Click and drag assets to move them'}
                {tool === 'draw_wall' && 'Click to place wall start and end points'}
                {tool === 'pan' && 'Click and drag to pan the view'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
  };

  return (
    <>
      <Tabs defaultValue="layouts" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="layouts">Office Layouts</TabsTrigger>
          <TabsTrigger value="racks">Rack Layouts</TabsTrigger>
        </TabsList>
        <TabsContent value="layouts" className="space-y-4">
          {officeLayoutsContent()}
        </TabsContent>
        <TabsContent value="racks" className="space-y-4">
          <RackLayouts clientId={clientId} />
        </TabsContent>
      </Tabs>

      {/* Dialogs moved outside TabsContent to fix Portal rendering */}
      {/* New Layout Dialog */}
      <Dialog open={showNewLayoutDialog} onOpenChange={setShowNewLayoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Layout</DialogTitle>
            <DialogDescription>Create a new floor or building layout</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newLayout.name}
                onChange={(e) => setNewLayout({ ...newLayout, name: e.target.value })}
                placeholder="e.g., Ground Floor, Building A"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newLayout.description}
                onChange={(e) => setNewLayout({ ...newLayout, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Width (px)</Label>
                <Input
                  type="number"
                  value={newLayout.width}
                  onChange={(e) => setNewLayout({ ...newLayout, width: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Height (px)</Label>
                <Input
                  type="number"
                  value={newLayout.height}
                  onChange={(e) => setNewLayout({ ...newLayout, height: parseInt(e.target.value) })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLayoutDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateLayout}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Asset Dialog */}
      <Dialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Asset to Layout</DialogTitle>
            <p className="text-sm text-slate-600 mt-1">
              Link an existing RMM asset or create a manual entry
            </p>
          </DialogHeader>
          <div className="space-y-4">
            {/* RMM Asset Selection - TOP PRIORITY */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-blue-900 font-semibold">Link RMM Asset (Recommended)</Label>
              <Select
                value={newAsset.linked_asset_id || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    // Manual entry - clear fields
                    setNewAsset({ 
                      ...newAsset, 
                      linked_asset_id: null, 
                      manual: true,
                      name: '',
                      device_type: 'desktop',
                      linked_user: ''
                    });
                  } else {
                    // Auto-populate from agent
                    const agent = agents.find(a => a.id === value);
                    if (agent) {
                      // Detect device type from agent data
                      let detectedType = 'desktop';
                      const hostname = (agent.hostname || '').toLowerCase();
                      const friendlyName = (agent.friendly_name || '').toLowerCase();
                      
                      if (hostname.includes('laptop') || friendlyName.includes('laptop')) {
                        detectedType = 'laptop';
                      } else if (hostname.includes('server') || friendlyName.includes('server')) {
                        detectedType = 'server';
                      } else if (hostname.includes('printer') || friendlyName.includes('printer')) {
                        detectedType = 'printer';
                      } else if (hostname.includes('phone') || friendlyName.includes('phone')) {
                        detectedType = 'phone';
                      } else if (hostname.includes('ap') || hostname.includes('router') || friendlyName.includes('access')) {
                        detectedType = 'access_point';
                      } else if (hostname.includes('camera') || friendlyName.includes('camera')) {
                        detectedType = 'camera';
                      }
                      
                      setNewAsset({ 
                        ...newAsset, 
                        linked_asset_id: value, 
                        manual: false,
                        name: agent.friendly_name || agent.hostname,
                        device_type: detectedType,
                        linked_user: agent.primary_user || ''
                      });
                    }
                  }
                }}
              >
                <SelectTrigger className="mt-2 bg-white">
                  <SelectValue placeholder="Select an RMM asset..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">None - Manual Entry</span>
                    </div>
                  </SelectItem>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${agent.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span>{agent.friendly_name || agent.hostname}</span>
                        {agent.primary_user && <span className="text-xs text-slate-500">({agent.primary_user})</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!newAsset.manual && newAsset.linked_asset_id && (
                <p className="text-xs text-green-700 mt-2">
                  ✓ Asset details auto-populated from RMM
                </p>
              )}
            </div>

            {/* Manual Fields - Only shown when needed */}
            <div className={`space-y-3 ${!newAsset.manual ? 'opacity-50' : ''}`}>
              <div>
                <Label>Asset Name *</Label>
                <Input
                  value={newAsset.name}
                  onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                  placeholder="e.g., Reception Desk PC"
                  disabled={!newAsset.manual}
                  className={!newAsset.manual ? 'bg-slate-100' : ''}
                />
                {!newAsset.manual && (
                  <p className="text-xs text-slate-500 mt-1">Auto-filled from RMM asset</p>
                )}
              </div>
              
              <div>
                <Label>Device Type</Label>
                <Select 
                  value={newAsset.device_type} 
                  onValueChange={(value) => setNewAsset({ ...newAsset, device_type: value })}
                  disabled={!newAsset.manual}
                >
                  <SelectTrigger className={!newAsset.manual ? 'bg-slate-100' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {deviceTypes.map(type => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" style={{ color: type.color }} />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {!newAsset.manual && (
                  <p className="text-xs text-slate-500 mt-1">Auto-detected from asset name</p>
                )}
              </div>

              <div>
                <Label>User/Location</Label>
                <Input
                  value={newAsset.linked_user || ''}
                  onChange={(e) => setNewAsset({ ...newAsset, linked_user: e.target.value })}
                  placeholder="e.g., John Doe, Reception"
                  disabled={!newAsset.manual}
                  className={!newAsset.manual ? 'bg-slate-100' : ''}
                />
                {!newAsset.manual && newAsset.linked_user && (
                  <p className="text-xs text-slate-500 mt-1">From RMM asset</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssetDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAsset} disabled={!newAsset.name}>
              Add Asset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OfficeLayouts;
